import { BaseColour, Image, ItemIdentifier, Tag, TagType } from '@conveyr/shared-types';
import type { components, operations } from '@/api/generated/openapi';

export type ListOrdersQuery = NonNullable<operations['list-orders']['parameters']['query']>;
export type ListOrdersResponse = operations['list-orders']['responses']['200']['content']['application/json'];
export type ListedOrderRaw = ListOrdersResponse['orders'][number];
export type ListedOrderItemRaw = ListedOrderRaw['order_items'][number];
export type ListedOrderInventoryItemRaw = components['schemas']['ListedOrderInventoryItem'];
export type ListedOrderItemIdentifierRaw = ListedOrderInventoryItemRaw['identifiers'][number];
export type ListedOrderItemImageRaw = ListedOrderInventoryItemRaw['images'][number];
export type ListedOrderCustomerRaw = components['schemas']['OrderCustomer'];
export type ListedOrderTagRaw = ListedOrderRaw['tags'][number];
export type OrderMessageRaw = components['schemas']['OrderMessage'];
export type ListedOrderQuotesRaw = components['schemas']['OrderQuotesSummary'];
export type ListedOrderQuoteRaw = ListedOrderQuotesRaw['quotes'][number];
export type ListedOrderQuoteItemRaw = ListedOrderQuoteRaw['quote_items'][number];
export type ListedOrderPickDataRaw = ListedOrderItemRaw['pick_data'];
export type ListedOrderPickScanRaw = NonNullable<Exclude<ListedOrderPickDataRaw, unknown[] | null>['scans']>[number];

// Wider than the documented `filters` (status and pack_assigned_to): callers
// have always been able to send any key and value, which the list ignores or
// rejects with a 422, so the type is not narrowed to hide that validation.
export type ListOrdersFilters = Record<string, string | number>;

export type ListedOrderStatus = ListedOrderRaw['status'];

export interface ListedOrderCustomer {
    id: number
    createdAt: string
    updatedAt: string
    name: string | null
    merchantId: number
    channelId: number | null
    defaultAddressId: number | null
    email: string | null
    phoneNumber: string | null
}

export interface ListedOrderInventoryItem {
    id: number
    uuid: string
    merchantId: number
    title: string
    merchantSku: string
    condition: ListedOrderInventoryItemRaw['condition']
    conditionNote: string | null
    lengthMm: number | null
    widthMm: number | null
    heightMm: number | null
    weightGm: number | null
    areDimensionsLocked: boolean | null
    deletedAt: string | null
    createdAt: string | null
    updatedAt: string | null
    identifiers: ItemIdentifier[]
    images: Image[]
}

export type ListedOrderPickScan
    = | { type: ListedOrderPickScanRaw['type'], code: string, scannedByUserId: number, scannedAt: string }
        | { type: ListedOrderPickScanRaw['type'], skippedByUserId: number, skippedAt: string };

export interface ListedOrderPickData {
    scans?: ListedOrderPickScan[]
    quantityPicked?: number
    toteCode?: string | null
}

export interface ListedOrderItem {
    id: number
    createdAt: string | null
    updatedAt: string | null
    orderId: number
    itemId: number
    listingId: number | null
    quantity: number
    pickedAt: string | null
    pickData: ListedOrderPickData | null
    externalId: string | null
    shipmentLineUuid: string
    item: ListedOrderInventoryItem
}

export interface OrderMessage {
    source: string
    text: string
}

// Carrier rates, parcels and totals stay in their wire form: nothing on the
// list reads them, and the legacy assignment payload has always sent them raw.
export interface ListedOrderQuoteItem {
    carrierRate: ListedOrderQuoteItemRaw['carrier_rate']
    parcels: ListedOrderQuoteItemRaw['parcels']
}

export interface ListedOrderQuote {
    id: string
    total: ListedOrderQuoteRaw['total']
    carrier: ListedOrderQuoteRaw['carrier']
    serviceLevel: ListedOrderQuoteRaw['service_level']
    provider: ListedOrderQuoteRaw['provider']
    deliveryDate?: string
    deliveryDays?: number
    quoteItems: ListedOrderQuoteItem[]
}

export interface ListedOrderQuotes {
    selectedQuoteId?: string
    cheapestQuoteId?: string
    fastestQuoteId?: string
    preferenceQuoteIds?: string[]
    quotes: ListedOrderQuote[]
    messages: OrderMessage[]
    errors?: OrderMessage[]
}

export interface ListedOrder {
    id: number
    createdAt: string | null
    updatedAt: string | null
    customerId: number
    customer: ListedOrderCustomer | null
    shippingAddressId: number
    billingAddressId: number
    orderId: string
    channelId: number
    channelOrderId: string | null
    orderDate: string
    shipBy: string | null
    arriveBy: string | null
    status: ListedOrderStatus
    warehouseNotes: string | null
    giftNote: string | null
    shippingMethodPreference: string | null
    pickAssignedTo: number | null
    packAssignedTo: number | null
    quotes: ListedOrderQuotes | null
    errors: OrderMessage[] | null
    currency: ListedOrderRaw['currency']
    toteCode: string | null
    shipmentUuid: string
    warehouseUuid: string
    orderItems: ListedOrderItem[]
    tags: Tag[]
}

const tagTypes: Record<ListedOrderTagRaw['type'], TagType> = {
    inbound_shipment: TagType.InboundShipment,
    item: TagType.Item,
    merchant: TagType.Merchant,
    order: TagType.Order,
    outbound_shipment: TagType.OutboundShipment,
};

const tagColours: Record<ListedOrderTagRaw['colour'], BaseColour> = {
    primary: BaseColour.Primary,
    indigo: BaseColour.Indigo,
    gray: BaseColour.Gray,
    red: BaseColour.Red,
    blue: BaseColour.Blue,
    green: BaseColour.Green,
    teal: BaseColour.Teal,
    amber: BaseColour.Yellow,
};

function toListedOrderCustomer(raw: ListedOrderCustomerRaw): ListedOrderCustomer {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        name: raw.name,
        merchantId: raw.merchant_id,
        channelId: raw.channel_id,
        defaultAddressId: raw.default_address_id,
        email: raw.email,
        phoneNumber: raw.phone_number,
    };
}

function toListedOrderItemIdentifier(raw: ListedOrderItemIdentifierRaw): ItemIdentifier {
    return {
        id: raw.id,
        identifier: raw.identifier,
        identifierType: raw.identifier_type,
    };
}

function toListedOrderItemImage(raw: ListedOrderItemImageRaw): Image {
    return {
        id: raw.id,
        largeUrl: raw.large_url,
        thumbnailUrl: raw.thumbnail_url,
    };
}

function toListedOrderInventoryItem(raw: ListedOrderInventoryItemRaw): ListedOrderInventoryItem {
    return {
        id: raw.id,
        uuid: raw.uuid,
        merchantId: raw.merchant_id,
        title: raw.title,
        merchantSku: raw.merchant_sku,
        condition: raw.condition,
        conditionNote: raw.condition_note,
        lengthMm: raw.length_mm,
        widthMm: raw.width_mm,
        heightMm: raw.height_mm,
        weightGm: raw.weight_gm,
        areDimensionsLocked: raw.are_dimensions_locked,
        deletedAt: raw.deleted_at,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        identifiers: raw.identifiers.map(toListedOrderItemIdentifier),
        images: raw.images.map(toListedOrderItemImage),
    };
}

function toListedOrderPickScan(raw: ListedOrderPickScanRaw): ListedOrderPickScan {
    if ('code' in raw) {
        return { type: raw.type, code: raw.code, scannedByUserId: raw.scanned_by_user_id, scannedAt: raw.scanned_at };
    }

    return { type: raw.type, skippedByUserId: raw.skipped_by_user_id, skippedAt: raw.skipped_at };
}

function toListedOrderPickData(raw: ListedOrderPickDataRaw): ListedOrderPickData | null {
    if (raw === null) return null;
    // The server returns pick data stored empty as [], which records no more than an empty object.
    if (Array.isArray(raw)) return {};

    return {
        scans: raw.scans?.map(toListedOrderPickScan),
        quantityPicked: raw.quantity_picked,
        toteCode: raw.tote_code,
    };
}

function toListedOrderQuoteItem(raw: ListedOrderQuoteItemRaw): ListedOrderQuoteItem {
    return {
        carrierRate: raw.carrier_rate,
        parcels: raw.parcels,
    };
}

function toListedOrderQuote(raw: ListedOrderQuoteRaw): ListedOrderQuote {
    return {
        id: raw.id,
        total: raw.total,
        carrier: raw.carrier,
        serviceLevel: raw.service_level,
        provider: raw.provider,
        deliveryDate: raw.delivery_date,
        deliveryDays: raw.delivery_days,
        quoteItems: raw.quote_items.map(toListedOrderQuoteItem),
    };
}

function toListedOrderQuotes(raw: ListedOrderQuotesRaw): ListedOrderQuotes {
    return {
        selectedQuoteId: raw.selected_quote_id,
        cheapestQuoteId: raw.cheapest_quote_id,
        fastestQuoteId: raw.fastest_quote_id,
        preferenceQuoteIds: raw.preference_quote_ids,
        quotes: raw.quotes.map(toListedOrderQuote),
        messages: raw.messages.map(toOrderMessage),
        errors: raw.errors?.map(toOrderMessage),
    };
}

function toListedOrderItem(raw: ListedOrderItemRaw): ListedOrderItem {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        orderId: raw.order_id,
        itemId: raw.item_id,
        listingId: raw.listing_id,
        quantity: raw.quantity,
        pickedAt: raw.picked_at,
        pickData: toListedOrderPickData(raw.pick_data),
        externalId: raw.external_id,
        shipmentLineUuid: raw.shipment_line_uuid,
        item: toListedOrderInventoryItem(raw.item),
    };
}

function toListedOrderTag(raw: ListedOrderTagRaw): Tag {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        name: raw.name,
        type: tagTypes[raw.type],
        colour: tagColours[raw.colour],
        isAvailableForMerchants: raw.is_available_for_merchants,
    };
}

function toOrderMessage(raw: OrderMessageRaw): OrderMessage {
    return {
        source: raw.source,
        text: raw.text,
    };
}

export function toListedOrder(raw: ListedOrderRaw): ListedOrder {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        customerId: raw.customer_id,
        customer: raw.customer === null ? null : toListedOrderCustomer(raw.customer),
        shippingAddressId: raw.shipping_address_id,
        billingAddressId: raw.billing_address_id,
        orderId: raw.order_id,
        channelId: raw.channel_id,
        channelOrderId: raw.channel_order_id,
        orderDate: raw.order_date,
        shipBy: raw.ship_by,
        arriveBy: raw.arrive_by,
        status: raw.status,
        warehouseNotes: raw.warehouse_notes,
        giftNote: raw.gift_note,
        shippingMethodPreference: raw.shipping_method_preference,
        pickAssignedTo: raw.pick_assigned_to,
        packAssignedTo: raw.pack_assigned_to,
        quotes: raw.quotes === null ? null : toListedOrderQuotes(raw.quotes),
        errors: raw.errors === null ? null : raw.errors.map(toOrderMessage),
        currency: raw.currency,
        toteCode: raw.tote_code,
        shipmentUuid: raw.shipment_uuid,
        warehouseUuid: raw.warehouse_uuid,
        orderItems: raw.order_items.map(toListedOrderItem),
        tags: raw.tags.map(toListedOrderTag),
    };
}
